/*
 * Button (Figma 119:13838). Values from get_design_context:
 * | Variant   | Padding        | Radius | Fill / effect                                  | Text                                      |
 * | primary   | 8px 12px       | 8px    | --button-primary-fill, --shadow-button-primary | 550 14/20, white, opacity .9, ss03        |
 * | ghost     | 6px 12px       | 8px    | none (hover --interaction-hover)               | 550 14/20, --text-default, opacity .9, ss03 |
 * | secondary | 8px 12px       | 8px    | --surface-raised, --shadow-button-secondary(-hover) | 550 14/20, --text-default, ss03      |
 * Gap 6px (label ↔ 16px icon). Focus: --focus-m (keyboard only).
 * Not in the design: disabled styling (opacity .5) and secondary layout (taken from primary);
 * on a touch screen (pointer: coarse) at least --size-touch-target (40px) high.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'

type Variant = 'primary' | 'secondary' | 'ghost'

const VARIANT: Record<Variant, string> = {
    primary:
        'py-[8px] [background:var(--button-primary-fill)] shadow-[var(--shadow-button-primary)] text-[var(--text-on-accent)]',
    secondary:
        'py-[8px] bg-[var(--surface-raised)] shadow-[var(--shadow-button-secondary)] hover:shadow-[var(--shadow-button-secondary-hover)] text-[var(--text-default)]',
    ghost: 'py-[6px] hover:bg-[var(--interaction-hover)] active:bg-[var(--interaction-active)] text-[var(--text-default)]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant
    /** 16px icon after the label (Figma "Icon" slot). */
    trailingIcon?: string
    children: ReactNode
}

export function Button({ variant = 'secondary', trailingIcon, className, children, type = 'button', ...rest }: ButtonProps) {
    return (
        <button
            type={type}
            className={cx(
                'inline-flex shrink-0 cursor-pointer items-center justify-center gap-[6px] overflow-clip rounded-[8px] px-[12px] pointer-coarse:min-h-[var(--size-touch-target)]',
                'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                VARIANT[variant],
                className,
            )}
            {...rest}
        >
            <span
                className={cx(
                    'ss03 whitespace-nowrap text-[14px] leading-[20px] font-[550]',
                    variant !== 'secondary' && 'opacity-90',
                )}
            >
                {children}
            </span>
            {trailingIcon && <Icon src={trailingIcon} size={16} />}
        </button>
    )
}
